<template>
  <div id="app">
    <header>
      <div class="header-image-container">
        <!-- Desktop/tablet GIF (hidden on mobile) -->
        <picture class="desktop-gif">
          <source :srcset="regularGifImage" type="image/gif">
          <img src="@/assets/2-1-7.png" alt="Header Image" class="header-image">
        </picture>
        
        <!-- Mobile-only GIF -->
        <picture class="mobile-gif">
          <source :srcset="mobileGifImage" type="image/gif">
          <img src="@/assets/2-1-7_BIG_GIF.gif" alt="Header Image" class="header-image">
        </picture>
      </div>
      <div class="date-display">{{ formattedDate }}</div>
      <nav class="menu">
        <a 
          v-for="item in menuItems" 
          :key="item"
          href="#"
          @click.prevent="currentPage = item"
          :class="['menu-item', { active: currentPage === item }]"
        >
          {{ item }}
        </a>
      </nav>
      <nav v-if="currentPage === 'Model'" class="sub-menu">
        <div class="sub-menu-items">
          <a 
            v-for="subItem in subMenuItems" 
            :key="subItem"
            href="#"
            @click.prevent="currentSubPage = subItem"
            :class="['sub-menu-item', { active: currentSubPage === subItem }]"
          >
            {{ subItem === 'Gameweek' ? 'Gameweek ' + fileNumber : subItem }}
          </a>
        </div>
        <div v-if="currentSubPage === 'Results'" class="results-summary">
          Wins: {{ wins }}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Losses: {{ losses }}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Win Percentage: {{ winPercentage }}%
        </div>
      </nav>
    </header>
    <div class="content-container">
      <div v-if="currentPage === 'About'" class="about-content">
        <p>
          The 2-1-7 Predictive Model is a football analytics tool inspired by the bold and unconventional 2-1-7 formation. Just as the formation prioritises relentless attacking play, this model is designed to take an aggressive, data-driven approach to predicting match outcomes and betting opportunities.
        </p>
        <p>
          Inspired by the work of pollster Nate Silver and FiveThirtyEight, the 2-1-7 model employs advanced statistical techniques, machine learning, and expert football insights to identify hidden patterns and evaluate probabilities. By blending rigorous data analysis with the art of the game, it seeks to demystify uncertainty and turn predictions into an edge.
        </p>
        <p>
          Whether you're looking for value bets, in-play opportunities, or a strategic advantage over the bookmakers, the 2-1-7 model ensures you're always attacking the odds with precision and confidence—just like the beautiful game itself. Learn more in <span class="blog-link" @click="openBlogPostByTitle('Moneyball: How to Become a Mid-Table Team')">this</span> blog post.
        </p>
        <footer class="email-footer">
          contact:&nbsp; <a href="mailto:info@2-1-7.com">info@2-1-7.com</a>
        </footer>
        <footer class="about-footer">
          website design:&nbsp; <a href="https://casp-ar.com" target="_blank">casp-ar.com</a>
        </footer>

      </div>
      <div v-if="currentPage === 'Blog'" class="blog-content" :class="{ 'compact-view': isCompactView }">
        <button class="collapse-toggle" @click="toggleView">
          {{ isCompactView ? '↓  expand' : '↑  collapse' }}
        </button>
        <article v-for="(post, index) in filteredBlogPosts" :key="index" class="blog-post" @click="handlePostClick(post)"
          :class="{ 'compact': isCompactView, 'pinned': post.pinned, 'sticky': isCompactView && post.pinned }">
          <h2 class="blog-title">
            {{ post.title }}
            <span v-if="post.pinned" class="pin-icon">&nbsp;📌</span>
          </h2>
          <p class="post-date" v-if="isCompactView">{{ formatDate(post.date) }}</p> 
          <p class="post-date" v-else>
            {{ formatDate(post.date) }}
            &nbsp;&nbsp;&nbsp;<span :class="{'red-dot': !post.clicked, 'green-dot': post.clicked}">&middot;</span>&nbsp;&nbsp;&nbsp;
            {{ post.readingTime }}
          </p> 
          <div class="post-content" v-if="!isCompactView">
            <p class="preview-text">{{ getPreviewText(post.content) }}</p>
          </div>
        </article>
      </div>
      <div v-if="currentPage === 'Model' && currentSubPage === 'Gameweek'" class="model-content">
        <div v-if="loading" class="loading">Loading...</div>
        <div v-else-if="error" class="error">{{ error }}</div>
        <div v-else>
          <table class="model-table">
            <thead>
              <tr>
                <th>Home&nbsp;&nbspTeam</th>
                <th></th>
                <th>Away&nbsp;&nbspTeam</th>
                <th>Handicap</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(match, index) in matches" :key="index" class="match-row">
                <td :class="{ 'home-underline': match['Suggested Handicap'].includes('Home') }">{{ processTeamName(match.H) }}</td>
                <td>v</td>
                <td :class="{ 'away-underline': match['Suggested Handicap'].includes('Away') }">{{ processTeamName(match.A) }}</td>
                <td>{{ match['Suggested Handicap'].includes('-0.0') ? 
                  match['Suggested Handicap'].replace('-0.0', ' 0.0') : 
                  match['Suggested Handicap'].replace(/(\d+\.?\d*)/, (num) => ' ' + (parseFloat(num) >= 0 ? '+' + num : num)) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-if="currentPage === 'Model' && currentSubPage === 'FAQ'" class="model-faq">
        <p>Information about the Gameweek and model details...<span class="blog-link" @click="openBlogPostByTitle('Welcome to 2-1-7')">this</span></p>
      </div>
      <div v-if="currentPage === 'Model' && currentSubPage === 'Results'" class="results-container">
        <div class="results-content">
          <table class="results-table">
            <thead>
              <tr>
                <th>Gameweek</th>
                <th>Home&nbsp;&nbspTeam</th>
                <th></th>
                <th>Away&nbsp;&nbspTeam</th>
                <th>Handicap</th>
                <th>Score</th>
                <th>Win</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="index in 25" :key="index" class="match-row">
                <td>GW {{ index }}</td>
                <td class="home-underline">Home Team {{ index }}</td>
                <td>v</td>
                <td class="away-underline">Away Team {{ index }}</td>
                <td>{{ index % 2 === 0 ? 'Home' : 'Away' }}</td>
                <td>0 - 0</td>
                <td :class="index % 2 === 0 ? 'win-circle-red' : 'win-circle-grey'">
                  ●
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <ModalComponent 
        :post="currentPost" 
        :isVisible="isModalVisible" 
        @close="closeModal"
        @share="sharePost"
        @post-missing="handlePostMissing"
        ref="blogModal" />
    </div>
  </div>
</template>

<script>
import ModalComponent from './components/ModalComponent.vue';
import axios from 'axios';
import regularGif from '@/assets/2-1-7_GIF.gif';
import mobileGif from '@/assets/2-1-7_BIG_GIF.gif';

export default {
  name: 'App',
  components: {
    ModalComponent,
  },
  data() {
    return {
      currentPage: 'Model',
      currentSubPage: 'Gameweek',
      menuItems: ['Model', 'Blog', 'About'],
      subMenuItems: ['Results', 'Gameweek', 'FAQ'],
      blogPosts: [],
      matches: [],
      fileNumber: null,
      loading: false,
      error: null,
      currentPost: null,
      isModalVisible: false,
      isCompactView: true,
      wins: 0,
      losses: 0,
      isMobile: false,
      regularGifImage: regularGif,
      mobileGifImage: mobileGif,
      postIdMap: {},
      currentPostId: null,
      isFirefox: false
    };
  },
  computed: {
    formattedDate() {
      const now = new Date();
      const months = ['JAN.', 'FEB.', 'MAR.', 'APR.', 'MAY', 'JUN.', 'JUL.', 'AUG.', 'SEP.', 'OCT.', 'NOV.', 'DEC.'];
      const month = months[now.getMonth()];
      const day = now.getDate();
      const year = now.getFullYear();
      return `${month} ${day}, ${year}`;
    },
    winPercentage() {
      const totalGames = this.wins + this.losses;
      return totalGames > 0 ? ((this.wins / totalGames) * 100).toFixed(2) : 0;
    },
    filteredBlogPosts() {
      const now = new Date();
      const filtered = this.blogPosts.filter(post => {
        // Extract components manually for more consistent parsing
        const parts = post.date.match(/(\w+)\.?\s(\d+),\s(\d+)/);
        if (!parts) return false;
        
        // Map month name to month number (0-11)
        const months = {
          'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
          'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
        };
        
        const month = months[parts[1].toUpperCase()];
        const day = parseInt(parts[2], 10);
        const year = parseInt(parts[3], 10);
        
        // Create date using components (more reliable cross-browser)
        const postDate = new Date(year, month, day);
        
        return postDate <= now;
      });
      
      return filtered;
    }
  },
  created() {
    // Detect Firefox
    this.isFirefox = /firefox/i.test(navigator.userAgent);
    
    // Add a global error handler
    window.addEventListener('error', this.handleGlobalError);
  },
  methods: {
    async fetchMatches() {
      this.loading = true;
      try {
        const apiUrl = `${process.env.VUE_APP_API_URL}/latest-file`;
        const response = await axios.get(apiUrl, {
          headers: {
            'x-api-key': process.env.VUE_APP_API_KEY,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        this.matches = response.data.data;
        this.fileNumber = response.data.file_number;
      } catch (err) {
        this.error = `Failed to load matches: ${err.message}`;
        console.error('Full error:', err);
      } finally {
        this.loading = false;
      }
    },
    async fetchBlogPosts() {
      try {
        const response = await axios.get('/blogPosts.json');
        
        // Check if response data exists and is an array
        if (!response.data || !Array.isArray(response.data)) {
          console.error('Invalid blog posts data format');
          return;
        }
        
        const posts = response.data;

        // Sort posts by pinned status and date
        this.blogPosts = posts.sort((a, b) => {
          if (a.pinned && !b.pinned) return -1;
          if (!a.pinned && b.pinned) return 1;
          return new Date(b.date) - new Date(a.date);
        });

        // Generate IDs after sorting
        this.generatePostIds();

        // Check for deep links after IDs are generated
        this.$nextTick(() => {
          this.handleHashChange();
        });
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      }
    },
    handlePostClick(post) {
      post.clicked = true;
      this.saveClickedState(post);
      this.openModal(post);
    },
    saveClickedState(post) {
      if (!post || !post.title) return;
      
      let clickedPosts = {};
      try {
        const stored = localStorage.getItem('clickedPosts');
        if (stored) {
          clickedPosts = JSON.parse(stored);
        }
      } catch (e) {
        console.error('Error parsing clickedPosts from localStorage:', e);
      }
      
      clickedPosts[post.title] = true;
      try {
        localStorage.setItem('clickedPosts', JSON.stringify(clickedPosts));
      } catch (e) {
        console.error('Error saving to localStorage:', e);
      }
    },
    openModal(post) {
      this.currentPost = post;
      this.isModalVisible = true;
    },
    closeModal() {
      this.isModalVisible = false;
      
      // Clear the current post after a delay to avoid flicker
      setTimeout(() => {
        this.currentPost = null;
        this.currentPostId = null;
      }, 300);
    },
    toggleView() {
      this.isCompactView = !this.isCompactView;
      // Find the blog-content container and scroll it to top
      const blogContent = document.querySelector('.blog-content');
      if (blogContent) {
        blogContent.scrollTop = 0;
      }
    },
    getPreviewText(content) {
      const textContent = content.replace(/\[image\]/g, '');
      const firstParagraph = textContent.split('\n\n')[0];
      return firstParagraph.length <= 350 ? firstParagraph : firstParagraph.substring(0, 347) + '...';
    },
    processTeamName(name) {
      return name.replace(/Nott'ham/g, 'Nottingham');
    },
    calculateResults() {
      // Example logic to calculate wins and losses
      this.wins = this.matches.filter(match => match.win).length;
      this.losses = this.matches.length - this.wins;
    },
    openBlogPostByTitle(title) {
      const post = this.blogPosts.find(p => p.title === title);
      if (post) {
        post.clicked = true;
        this.saveClickedState(post);
        this.openModal(post);
      } else {
        console.warn(`Blog post with title "${title}" not found.`);
      }
    },
    formatDate(dateString) {
      // Check if we're in compact view and window width is below threshold
      if (this.isCompactView && window.innerWidth <= 600) {
        // Truncate date to everything before the comma (e.g., "JAN. 19")
        const commaPos = dateString.indexOf(',');
        if (commaPos > 0) {
          return dateString.substring(0, commaPos);
        }
        // Fallback to a shorter substring
        return dateString.substring(0, 8);
      }
      // Return full date for expanded view or larger screens
      return dateString;
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 480;
    },
    handleWindowResize() {
      // Force a re-render of the component
      this.$forceUpdate();
    },
    generatePostIds() {
      // Reset the map
      this.postIdMap = {};
      
      // Track posts by date to handle duplicates
      const postsByDate = {};
      
      // Process each post
      if (!this.blogPosts || this.blogPosts.length === 0) {
        return;
      }
      
      // First, sort posts by pinned status and date to maintain proper order
      const sortedPosts = [...this.blogPosts].sort((a, b) => {
        // First by pinned status (pinned posts first)
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        
        // Then by pinPriority if both are pinned
        if (a.pinned && b.pinned) {
          if (a.pinPriority && b.pinPriority) {
            return a.pinPriority - b.pinPriority;
          }
          if (a.pinPriority) return -1;
          if (b.pinPriority) return 1;
        }
        
        // Finally by date (newest first)
        return new Date(b.date) - new Date(a.date);
      });
      
      // Now generate IDs while preserving the order
      sortedPosts.forEach(post => {
        // Skip if no date
        if (!post.date) {
          return;
        }
        
        // Parse the date (assuming format like "MAR. 14, 2025")
        const dateParts = post.date.match(/(\w+)\.\s+(\d+),\s+(\d+)/);
        if (!dateParts) {
          return;
        }
        
        const month = this.getMonthNumber(dateParts[1]);
        const day = parseInt(dateParts[2]);
        const year = parseInt(dateParts[3].substring(2)); // Get last 2 digits
        
        // Create date key in format YYMMDD
        const dateKey = `${year.toString().padStart(2, '0')}${month.toString().padStart(2, '0')}${day.toString().padStart(2, '0')}`;
        
        // Initialize array for this date if it doesn't exist
        if (!postsByDate[dateKey]) {
          postsByDate[dateKey] = [];
        }
        
        // Add post to the array for this date
        postsByDate[dateKey].push(post);
        
        // Generate ID with counter suffix if needed
        const counter = postsByDate[dateKey].length;
        const postId = counter > 1 ? `${dateKey}_${counter.toString().padStart(2, '0')}` : dateKey;
        
        // Store in map for lookup
        this.postIdMap[postId] = post;
        
        // Add ID to the post object for reference
        post.postId = postId;
      });
      
      // Ensure the blogPosts array maintains its original order and pinned status
      this.blogPosts = sortedPosts;
    },
    getMonthNumber(monthAbbr) {
      const months = {
        'JAN': 1, 'FEB': 2, 'MAR': 3, 'APR': 4, 'MAY': 5, 'JUN': 6,
        'JUL': 7, 'AUG': 8, 'SEP': 9, 'OCT': 10, 'NOV': 11, 'DEC': 12
      };
      return months[monthAbbr.toUpperCase().replace('.', '')] || 1;
    },
    handleHashChange() {
      this.currentHash = window.location.hash;
      
      // Check if hash contains a blog post ID
      if (this.currentHash.startsWith('#post-')) {
        const postId = this.currentHash.substring(6); // Remove '#post-' prefix
        this.openBlogPostById(postId);
      }
    },
    openBlogPostById(postId) {
      // Find the post with matching ID
      const post = this.postIdMap[postId];
      if (post) {
        this.openBlogPost(post);
      }
    },
    openBlogPost(post) {
      if (!post) {
        return;
      }
      
      // Store the post ID for recovery if needed
      this.currentPostId = post.postId;
      
      // Create a deep copy of the post to avoid reference issues
      const postCopy = JSON.parse(JSON.stringify(post));
      
      // Firefox-specific handling
      if (this.isFirefox) {
        this.currentPost = postCopy;
        
        // Force a data update before showing the modal
        this.$nextTick(() => {
          // Ensure the modal component has the post data
          if (this.$refs.blogModal) {
            this.$refs.blogModal.setPostDirectly(postCopy);
          }
          
          // Show the modal after a short delay
          setTimeout(() => {
            this.isModalVisible = true;
          }, 100);
        });
      } else {
        // Normal handling for other browsers
        this.currentPost = postCopy;
        this.isModalVisible = true;
      }
    },
    handleGlobalError(error) {
      console.error('[App] Global error:', error);
    },
    handlePostMissing() {
      // Try to recover by finding the post again
      if (this.currentPostId) {
        const recoveredPost = this.posts.find(p => p.postId === this.currentPostId);
        
        if (recoveredPost) {
          // Create a fresh copy
          const postCopy = JSON.parse(JSON.stringify(recoveredPost));
          this.currentPost = postCopy;
          
          // Directly set the post on the modal component
          if (this.$refs.blogModal) {
            this.$refs.blogModal.setPostDirectly(postCopy);
          }
          
          // Force a re-render
          this.$nextTick(() => {
            // Toggle visibility to force re-render
            const wasVisible = this.isModalVisible;
            this.isModalVisible = false;
            
            setTimeout(() => {
              this.isModalVisible = wasVisible;
            }, 50);
          });
        } else {
          this.closeModal();
        }
      } else {
        this.closeModal();
      }
    },
    sharePost(post) {
      // Extract date in YYMMDD format
      const dateStr = post.date;
      let formattedDate = '';
      
      // Parse date string like "MAR. 14, 2025"
      if (dateStr) {
        const months = {
          'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04', 'MAY': '05', 'JUN': '06',
          'JUL': '07', 'AUG': '08', 'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
        };
        
        // Extract parts using regex
        const match = dateStr.match(/([A-Z]{3})\.?\s+(\d{1,2}),?\s+(\d{4})/i);
        
        if (match) {
          const month = months[match[1].toUpperCase()];
          const day = match[2].padStart(2, '0');
          const year = match[3].slice(2); // Get last 2 digits of year
          
          formattedDate = year + month + day; // Format as YYMMDD
        }
      }
      
      // Create the share URL with the correct format
      const baseUrl = window.location.origin;
      const shareUrl = `${baseUrl}#post-${formattedDate}`;
      
      // Copy to clipboard
      this.copyToClipboard(shareUrl);
      console.log(`Copied link: ${shareUrl}`); // For verification
    },
    copyToClipboard(text) {
      // Try modern clipboard API first
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        navigator.clipboard.writeText(text)
          .then(() => {
            this.showNotification('link copied to clipboard');
          })
          .catch(err => {
            this.fallbackCopy(text);
          });
      } else {
        // Fallback for older browsers or Safari
        this.fallbackCopy(text);
      }
    },
    fallbackCopy(text) {
      try {
        // Create temporary input element
        const el = document.createElement('textarea');
        el.value = text;
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        
        // Select and copy
        const selected = document.getSelection().rangeCount > 0 
          ? document.getSelection().getRangeAt(0) : false;
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        
        // Restore selection if needed
        if (selected) {
          document.getSelection().removeAllRanges();
          document.getSelection().addRange(selected);
        }
        
        this.showNotification('Link copied to clipboard!');
      } catch (err) {
        this.showNotification('Share URL: ' + text);
      }
    },
    showNotification(message, duration = 2000) {
      // Create notification element
      const notification = document.createElement('div');
      notification.textContent = message;
      notification.style.position = 'fixed';
      notification.style.bottom = '50dvh';
      notification.style.left = '50dvw';
      notification.style.transform = 'translateX(-50%)';
      notification.style.backgroundColor = 'white';
      notification.style.color = '#e72100';
      notification.style.padding = '5px 20px';
      notification.style.zIndex = '9999';
      notification.style.opacity = '0';
      notification.style.transition = 'opacity 0.3s';
      notification.style.fontFamily = "'Castoro', serif";
      notification.style.fontSize = '14px';
      notification.style.fontStyle = 'italic';
      notification.style.border = '1px solid #e72100';
      
      // Add to document
      document.body.appendChild(notification);
      
      // Fade in
      setTimeout(() => {
        notification.style.opacity = '1';
      }, 10);
      
      // Remove after duration
      setTimeout(() => {
        notification.style.opacity = '0';
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 300);
      }, duration);
    },
  },
  mounted() {
    if (this.currentPage === 'Model') {
      this.fetchMatches();
    }
    this.fetchBlogPosts();
    this.calculateResults();
    
    // Check if mobile on initial load
    this.checkMobile();
    
    // Add resize listener for date formatting
    window.addEventListener('resize', this.handleWindowResize);
    
    // Generate unique date-based IDs for all posts
    this.generatePostIds();
    
    // Log when a post is opened
    this.$watch('currentPost', (newPost) => {
      if (newPost) {
      }
    });
  },
  beforeUnmount() {
    // Clean up
    window.removeEventListener('resize', this.checkMobile);
    window.removeEventListener('resize', this.handleWindowResize);
    window.removeEventListener('error', this.handleGlobalError);
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Castoro:ital@0;1&display=swap');

#app {
  text-align: center;
  font-family: 'Castoro', serif;
}

header {
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
  padding: 20px 0 15px 0;
}

/* Base header styling for laptops/desktops */
.header-image-container,
.header-image {
  width: auto;
  max-height: min(14dvh, 120px); /* Baseline for larger screens */
  transition: opacity 0.3s ease;
}

/* Tablet-specific adjustments */
@media (max-width: 768px) and (min-width: 481px) {
  .header-image-container,
  .header-image {
    max-height: min(12dvh, 110px) !important; /* Slightly smaller on tablets */
  }
  
  header {
    padding: 15px 0 10px 0; /* Slightly reduced padding */
  }
}

/* Keep existing mobile styles */
@media (max-width: 480px) and (min-width: 321px) {
  header {
    padding: 10px 0 8px 0;
  }
  
  .header-image-container,
  .header-image {
    max-height: min(10dvh, 80px) !important;
  }
}

/* Extra small devices */
@media (max-width: 320px) {

  
  .header-image {
    max-height: 10dvh !important; 
  }
}

.blog-title {
  font-size: 1.1em;
  font-weight: normal;
  color: #333;
  margin-bottom: 5px;
  position: relative;
  display: inline-block;
}

.blog-title:hover::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -2px;
  height: 1.5px;
  background-color: #e72100;
  transition: all 0.2s ease;
}

.preview-text {
  font-size: 0.9em; 
  color: #4d4d4d;
  margin-bottom: 10px;
  line-height: 1.5;
  text-align: left;
}

.post-date {
  color: #666;
  font-size: 0.8em;
  margin-top: -15px;
  margin-bottom: -15px;
}

.blog-title:hover + .post-date {
  text-decoration: underline; /* Underline the date when the title is hovered */
}

.blog-post {
  text-align: left;
  position: relative;
  z-index: 1; /* Default z-index */
}

.date-display {
  position: sticky;
  top: 85px;
  background: white;
  z-index: 2;
  padding: 5px 0;
  color: #666;
  font-size: 0.8em;
}

.menu {
  position: sticky;
  top: 110px;
  background: white;
  z-index: 2;
  display: flex;
  justify-content: center;
  gap: 30px;
  padding: 10px 0;
}

.menu-item {
  text-decoration: none;
  color: #333;
  padding: 5px 15px;
  font-size: 1.4em;
}

.menu-item:hover {
  color: #666;
}

.menu-item.active {
  border-bottom: 3px solid #e72100
}

.about-content,
.blog-content,
.model-content {
  position: relative;
  z-index: 1;
}

.about-content {
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 40px auto;
  padding: 0 20px;
  line-height: 1.6;
  text-align: justify; /* Add justification by default */
}

/* Remove justification on mobile */
@media screen and (max-width: 480px) {
  .about-content {
    text-align: left; /* Reset to left alignment on mobile */
  }
}

.about-footer {
  text-align: left;
  width: 100%;
  padding-top: 40px;
}

.about-footer a {
  text-decoration: none;
  color: #333;
}

.about-footer a:hover {
  color: #e72100;
  text-decoration: underline;
}

.email-footer {
  padding-top: 0; /* Reduce padding between footers */
  
}

.email-footer a {
  text-decoration: none;
  color: #333;
}

.email-footer a:hover {
  color: #e72100;
  text-decoration: underline;
}

.blog-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
  height: calc(70dvh - 10vmin); /* More proportionate to screen size */
  overflow-y: auto;
  border-top: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
  position: relative;
  z-index: 2; /* Higher than blog posts */
  margin-top: 10px;
}

/* Media queries for different screen sizes */
@media (min-height: 900px) {
  .blog-content,
  .blog-content.compact-view,
  .results-container {
    height: calc(75dvh - 12vmin);
  }
}

@media (max-height: 600px) {
  .blog-content,
  .blog-content.compact-view,
  .results-container {
    height: calc(65dvh - 12vmin);
  }
}

.blog-content::-webkit-scrollbar-track {
  background: #f1f1f1; /* Background of the scrollbar track */
}

.blog-content::-webkit-scrollbar-thumb {
  background-color: #e72100; /* Set the scrollbar thumb color to the specified red */
  border-radius: 10px; /* Rounded corners */
  border: 2px solid #f1f1f1; /* Padding around the thumb */
}

/* Firefox */
.blog-content {
  scrollbar-width: auto; /* Use default scrollbar width */
  scrollbar-color: #e72100 #f1f1f1; /* Thumb color and track color */
}

.model-content {
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
}

.model-table {
  width: 80%;
  border-collapse: collapse;
  font-family: 'Castoro', serif;
  margin: 0 auto;
}

.model-table td {
  padding: 6px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  font-size: 0.9em;
  font-weight: 300;
  color: #000000;
}

.model-table th {
  font-size: 1em;
  text-align: left;
  padding: 6px;
  font-weight: normal;
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 10;
  border-bottom: none;
}

.model-table th::after {
  content: '';
  display: block;
  width: 130%;
  height: 1px;
  background-color: #ccc;
  position: absolute;
  bottom: -1px;
  left: -15%;
  z-index: 15;
}

.model-table tr:hover {
  background-color: #f5f5f596;
}

.model-table td.home-team::after,
.model-table td.away-team::after {
  content: ' v';
  color: inherit; 
}

/* Add container for all main content */
.content-container {
  position: relative;
  z-index: 1;
}

/* WebKit-based browsers (including Safari) */
::-webkit-scrollbar {
  width: auto; /* Set the scrollbar width */
}

::-webkit-scrollbar-track {
  background: #f1f1f1; /* Background of the scrollbar track */
}

::-webkit-scrollbar-thumb {
  background-color: #e72100; /* Set the scrollbar thumb color to the specified red */
  border-radius: 10px; /* Rounded corners */
  border: 2px solid #f1f1f1; /* Padding around the thumb */
}

/* Firefox */
* {
  scrollbar-width: auto; /* Use default scrollbar width */
  scrollbar-color: #e72100 #f1f1f1; /* Thumb color and track color */
}

/* Add styles for the blog image */
.blog-image {
  width: 100%;
  height: auto;
  margin-bottom: 15px;
}

.collapse-toggle {
  position: sticky;
  top: 10px;
  padding-left: 20px;
  padding-right: 20px;
  background: none;
  border: 1px solid #e72100;
  color: #e72100;
  cursor: pointer;
  font-family: 'Castoro', serif;
  font-style: italic;
  transition: all 0.3s ease;
  z-index: 10;
  font-size: 0.9em;
 
  
}

.collapse-toggle:hover {
  background: #e72100;
  color: white;
}

.blog-post.compact {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 13px;
  margin: 0;
  border: none;
}

.blog-post.compact .blog-title {
  margin: 0;
}

.blog-post.compact .post-date {
  margin: 0;
}

.blog-title:hover + .post-date {
  text-decoration: underline; /* Underline the date when the title is hovered */
}

.blog-post.pinned {
  background-color: white;
  border-bottom: 1px solid #e72100;
  margin-right: -1px;
  margin-left: -1px;
}

.blog-post.sticky {
  position: sticky;
  z-index: 5;
}

/* First pinned post sticks to the top */
.blog-post.sticky:nth-of-type(1) {
  top: 0px;
}

/* Second pinned post sticks below the first one */
.blog-post.sticky:nth-of-type(2) {
  top: 46px; /* Adjust this value based on the height of your pinned posts */
}

/* Third pinned post sticks below the second one */
.blog-post.sticky:nth-of-type(3) {
  top: 92px; /* Double the previous value */
}

/* Fourth pinned post sticks below the third one */
.blog-post.sticky:nth-of-type(4) {
  top: 138px; /* Double the previous value */
}

.red-dot {
  color: #e72100;
  font-size: 3em;
  vertical-align: -5px;
}

.green-dot {
  color: #666; /* Change to green when clicked */
  font-size: 3em;
  vertical-align: -5px;
}

.blog-content .post-date {  /* Only applies to posts in the main blog content */
  color: #666;
  font-size: 0.8em;
  margin-top: -15px;
  margin-bottom: -20px;
}

.modal-content .post-date {  /* Overrides for modal view */
  margin-top: 0;
  margin-bottom: 10px;
}

.api-data {
  text-align: left;
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
}

.message {
  font-size: 1.2em;
  margin-bottom: 10px;
}

.timestamp {
  color: #666;
  font-style: italic;
}

.model-table td:nth-child(1) { /* Home team column */
  width: 20%;
}

.model-table td:nth-child(2) { /* 'v' column */
  text-align: center;
  width: 20%;
}

.model-table td:nth-child(3) { /* Away team column */
  width: 40%;
}

.model-table td:nth-child(4) { /* Handicap column */
  width: 20%;
}

.match-row {
  position: relative; /* Ensure the row is positioned relative for absolute positioning */
}

.model-table tr:hover .home-underline::after,
.model-table tr:hover .away-underline::after {
  content: '•';
  color: #e72100;
  position: absolute;
  right: -23px; 
  font-size: 1.2em; 
  pointer-events: none;
}

.model-table tr:hover .away-underline::after {
  left: -20px; 
}

.home-underline, .away-underline {
  position: relative;
}

.sub-menu {
  display: flex;
  flex-direction: column; /* Stack items vertically */
  align-items: center;
  padding: 10px 0;
  background: #ffffff;
}

.sub-menu-items {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.sub-menu-item {
  text-decoration: none;
  color: #666;
  padding: 5px 10px;
  font-size: 0.9em;
  font-style: italic;
  font-weight: 300;
}

.sub-menu-item.active {
  color: #e72100;
}

.results-summary {
  font-size: 0.8em;
  font-weight: normal;
  font-style: italic;
  color: #666;
  margin-top: 10px; /* Space between items and summary */
  pointer-events: none; /* Make non-interactive */
}

.results-table {
  width: 90%;
  border-collapse: collapse;
  font-family: 'Castoro', serif;
  margin: 0 auto;
}

.results-table td {
  padding: 6px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  font-size: 0.9em;
  font-weight: 300;
  color: #000000;
}

.results-table th {
  font-size: 1em;
  text-align: left;
  font-weight: normal;
  position: sticky;
  top: -1px;
  background-color: white;
  z-index: 15;
}

.results-table th::after {
  content: '';
  display: block;
  width: 130%;
  height: 1px; /* Thickness of the line */
  background-color: #ccc; /* Color of the line */
  position: absolute;
  bottom: -1px; /* Position it just below the header */
  left: -15%;
  z-index: 10; /* Ensure it appears below the header content */
}

.results-table tr:hover {
  background-color: #f5f5f596;
}

.results-table td:nth-child(1) { /* Gameweek column */
  width: 17%;
}

.results-table td:nth-child(2) { /* Home team column */
  width: 17%;
}

.results-table td:nth-child(3) { /* 'v' column */
  text-align: left;
  width: 5%;
}

.results-table td:nth-child(4) { /* Away team column */
  width: 20%;
}

.results-table td:nth-child(5) { /* Handicap column */
  width: 15%;
}

.results-table td:nth-child(6) { /* Score column */
  width: 10%;
  padding-left: 10px
}

.results-table td:nth-child(7) { /* Win column */
  width: 10%;
  text-align: left; /* Align left */
  padding-left: 20px; /* Offset to the right */
}

.results-container {
  max-width: 800px;
  margin: 0 auto;
  overflow-y: auto;
  z-index: 1;
  position: relative;
  height: calc(70dvh - 10vmin); /* Match blog-content height */
  border-bottom: 1px solid #ccc;
}

.results-content {
  position: relative;
  z-index: 1; /* Ensure content is behind the header */
}

.win-circle-red {
  color: #e72100 !important; /* Red color for circle */
}

.win-circle-grey {
  color: #666 !important; /* Grey color for circle */
}

.blog-link {
  color: #e72100;
  cursor: pointer;
  text-decoration: underline;
}

.blog-link:hover {
  color: #333;
}

.blog-quote {
  font-style: italic;
  border-left: 3px solid #e72100;
  padding-left: 15px;
  margin: 20px 0;
  color: #666;
}

/* Container for blog content in compact mode */
.blog-content.compact-view {
  position: sticky;
  height: calc(70dvh - 10vmin); /* Match standard blog-content height */
  overflow-y: auto;
}

/* Styling for pinned posts only in compact view */
.blog-content.compact-view .blog-post.pinned {
  background-color: white;
  border-bottom: 1px solid #e72100;
  margin-right: -1px;
  margin-left: -1px;
}

/* Styling for sticky posts only in compact view */
.blog-content.compact-view .blog-post.sticky {
  position: sticky;
  z-index: 5;
}

/* In expanded view, reset pinned posts to normal behavior */
.blog-content:not(.compact-view) .blog-post.pinned {
  background-color: transparent;
  border-bottom: none;
  margin-right: 0;
  margin-left: 0;
  position: relative;
  z-index: 1;
}

/* Ensure blog-content border is above all content */
.blog-content {
  border-top: 1px solid #ccc;
  position: relative;
  z-index: 100; /* Higher than blog posts */
}

/* Media queries for different screen sizes - apply to both views */
@media (min-height: 900px) {
  .blog-content,
  .blog-content.compact-view,
  .results-container {
    height: calc(75dvh - 12vmin);
  }
}

@media (max-height: 600px) {
  .blog-content,
  .blog-content.compact-view,
  .results-container {
    height: calc(65dvh - 12vmin);
  }
}

/* Separate sizing for gameweek and results tables */
.model-content .model-table-container,
.results-container {
  height: calc(65dvh - 12vmin); /* Different proportion for tables */
}

/* Media queries for tables */
@media (min-height: 900px) {
  .model-content .model-table-container,
  .results-container {
    height: calc(70dvh - 10vmin);
  }
}

@media (max-height: 600px) {
  .model-content .model-table-container,
  .results-container {
    height: calc(60dvh - 14vmin);
  }
}

/* Fix date display in both compact and expanded views */
@media (max-width: 480px) {
  /* Compact view styling */
  .blog-post.compact {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px 8px 10px;
  }
  
  .blog-post.compact .blog-title {
    margin-right: 12px;
    font-size: 0.9em;
  }
  
  .blog-post.compact .post-date {
    text-align: right;
    min-width: 60px;
    font-size: 0.75em;
    white-space: nowrap;
    overflow: visible;
  }
  
  /* Expanded view styling */
  .blog-post:not(.compact) .post-date {
    width: auto;
    max-width: none;
    white-space: normal;
    overflow: visible;
    font-size: 0.75em;
  }
}

/* Very small devices */
@media (max-width: 320px) {
  .blog-post.compact .blog-title {
    font-size: 0.85em; /* Even smaller title */
  }
  
  .blog-post.compact .post-date {
    font-size: 0.65em; /* Even smaller date */
  }
  
  .blog-post.compact .preview-text {
    font-size: 0.75em; /* Even smaller preview */
  }
}

/* Update sticky post behavior to only apply under 600px */
@media (max-width: 600px) {
  /* First, reset ALL pinned/sticky posts to not be sticky */
  .blog-content.compact-view .blog-post.sticky,
  .blog-content.compact-view .blog-post.pinned {
    position: relative !important;
    top: auto !important;
    z-index: 1 !important;
  }
  
  /* Then, ONLY make the first one sticky */
  .blog-content.compact-view .blog-post.sticky:first-of-type {
    position: sticky !important;
    top: 0px !important; /* Adjust for submenu height */
    z-index: 8 !important;
    background-color: white !important;
    border-bottom: 1px solid #e72100 !important;
  }
  
  /* Explicitly target all other sticky posts to ensure they don't stick */
  .blog-content.compact-view .blog-post.sticky:not(:first-of-type) {
    position: relative !important;
    top: auto !important;
    z-index: 1 !important;
  }
  
  /* Adjust the sticky post appearance for better visibility */
  .blog-content.compact-view .blog-post.sticky:first-of-type .blog-title {
    font-weight: normal !important;
  }
  
  /* Ensure proper spacing for content below sticky post */
  .blog-content.compact-view .blog-post:not(:first-of-type) {
    margin-top: 0 !important;
  }
      /* Only apply when About page is active */
      body:has(.about-content) .menu {
    border-bottom: 1px solid #e0e0e0 !important; /* Light grey line */
    margin-bottom: -8px !important; /* Space after the line */
    padding-bottom: 15px !important; /* Space before the line */
  }
  
  /* Alternative approach if :has selector isn't supported */
  .menu + .content-container .about-content {
    position: relative;
    margin-top: 5px !important;
  }
  
  .menu + .content-container .about-content::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    background-color: #e0e0e0; /* Light grey color */
    width: 100%;
  }
}

/* Base styles for header images */
.header-image-container {
  position: relative;
  width: auto;
  display: inline-block;
}

/* Desktop GIF - visible by default */
.desktop-gif {
  display: block;
}

/* Mobile GIF - hidden by default */
.mobile-gif {
  display: none;
}

/* Show mobile GIF only on mobile devices */
@media (max-width: 325px) {
  .desktop-gif {
    display: none; /* Hide desktop GIF */
  }
  
  .mobile-gif {
    display: block; /* Show mobile GIF */
  }

  .date-display {
    display: none;
  }
}

/* Ensure both are hidden in landscape mode where we hide the header */
@media (max-height: 600px) and (orientation: landscape) {
  .desktop-gif, .mobile-gif {
    display: none;
  }

}

/* Add grey line beneath menu items on About page for mobile */
@media (max-width: 600px) {
  /* Only apply when About page is active */
  body:has(.about-content) .menu {
    border-bottom: 1px solid #e0e0e0 !important; /* Light grey line */
    margin-bottom: -8px !important; /* Space after the line */
    padding-bottom: 15px !important; /* Space before the line */
  }
  
  /* Alternative approach if :has selector isn't supported */
  .menu + .content-container .about-content {
    position: relative;
    margin-top: 5px !important;
  }
  
  .menu + .content-container .about-content::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    background-color: #e0e0e0; /* Light grey color */
    width: 100%;
  }
}

/* Hide header and make menus sticky on mobile landscape */
@media (max-height: 600px) and (orientation: landscape) {
  /* Hide header to save vertical space */
  .header-image-container {
    display: none !important;
  }
  
  /* Make main menu sticky */
  .menu {
    position: sticky !important;
    top: 0 !important;
    z-index: 100 !important;
    background-color: white !important;
    margin-top: -40px !important;
  }
  
  /* Make sub-menu sticky and positioned below main menu */
  .sub-menu {
    position: sticky !important;
    top: 30px !important; /* Adjust based on your main menu height */
    z-index: 99 !important;
    background-color: white !important;
    padding-top: 2px !important;
    padding-bottom: 2px !important;
    margin-top: 0 !important;
  }
  
  /* Adjust content container to account for sticky menus */
  .content-container {
    padding-top: 5px !important;
  }
}

@media (max-height: 400px) and (orientation: landscape) {
  /* First, reset ALL pinned/sticky posts to not be sticky */
  .blog-content.compact-view .blog-post.sticky,
  .blog-content.compact-view .blog-post.pinned {
    position: relative !important;
    top: auto !important;
    z-index: 1 !important;
  }
  
  /* Then, ONLY make the first one sticky */
  .blog-content.compact-view .blog-post.sticky:first-of-type {
    position: sticky !important;
    top: 0px !important; /* Adjust for submenu height */
    z-index: 8 !important;
    background-color: white !important;
    border-bottom: 1px solid #e72100 !important;
  }
  
  /* Explicitly target all other sticky posts to ensure they don't stick */
  .blog-content.compact-view .blog-post.sticky:not(:first-of-type) {
    position: relative !important;
    top: auto !important;
    z-index: 1 !important;
  }
  
  /* Make the sticky post title bold to distinguish it */
  .blog-content.compact-view .blog-post.sticky:first-of-type .blog-title {
    font-weight: bold !important;
  }
  
  /* Hide header image only */
  .header-image-container {
    display: none !important;
  }
}

/* Fix menu positioning on Model page for extra small devices */
@media (max-width: 320px) {
  /* Prevent menu jump by fixing its position */
  .menu {
    position: relative !important;
    top: 0 !important;
    transition: none !important; /* Disable any transitions that might cause jumps */
  }
  
  /* Fix specific issue with Model page content pushing menu down */
  .model-content {
    margin-top: 0 !important;
    padding-top: 0 !important;
  }
  
  /* Ensure content container doesn't affect menu position */
  .content-container {
    margin-top: 0 !important;
    padding-top: 5px !important;
  }
  
  /* Fix any height changes in header that might affect menu */
  header {
    height: auto !important;
    min-height: auto !important;
  }
  
  /* Ensure consistent spacing between menu and content */
  .menu + .content-container {
    margin-top: 5px !important;
  }
  
  /* Prevent any animations or transitions on page change */
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.1s !important;
  }
}

/* Additional fix for landscape orientation */
@media (max-height: 400px) and (orientation: landscape) {
  /* Ensure consistent menu positioning across all pages */
  .model-content {
    margin-top: 0 !important;
  }
  
  /* Fix menu position on model page */
  body:has(.model-content) .menu {
    margin-top: 0 !important;
  }
}

.faq-content {
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 40px auto;
  padding: 0 20px;
  line-height: 1.6;
  text-align: justify; /* Add justification by default */
}

/* Remove justification on mobile */
@media screen and (max-width: 480px) {
  .faq-content {
    text-align: left; /* Reset to left alignment on mobile */
  }
}

.faq-content p {
  text-align: justify; /* Ensure paragraphs are justified */
}

/* Remove justification on mobile */
@media screen and (max-width: 480px) {
  .faq-content p {
    text-align: left; /* Reset to left alignment on mobile */
  }
}

</style>


